import React, { useMemo, useState, useEffect } from 'react';
import { Search, ClipboardList, Calendar, Info, Package, Plus, X, ListFilter, Check, FileText, Lock, Unlock, User, Download, Printer, History, ShoppingBag } from 'lucide-react';
import { Order, Store, OrderSheet, OrderStatus, Product, SellerOrderSortBy } from '../../../types';
import { getCustomerReceipts } from '../../../api/reports';
import { getSellerOrdersPage } from '../../../api/seller-pages';

import { OrderListView } from './OrderListView';
// @ts-ignore
import html2pdf from 'html2pdf.js';
import { PackingWaitingListView } from './PackingWatingListView';
import { FieldChecklistView } from './FieldCheckListView';
import { PackingLabelsView, PACKING_LABEL_STYLES } from './PackingLabelsView';
import { CustomerReceiptsView } from '../CustomerReceiptsView';
import { OrderSummaryCard } from './OrderSummaryCard';
import { CompletedPickupListView } from './CompletedPickupListView';

type ReportType =
  | 'ORDER_LIST'
  | 'CUSTOMER_RECEIPTS'
  | 'PACKING_WAITING_LIST'
  | 'PACKING_LABELS'
  | 'FIELD_CHECKLIST'
  | 'COMPLETED_PICKUP_LIST';
interface OrdersTabProps {
  orders: Order[];
  ordersSummary?: {
    totalOrders: number;
    totalAmount: number;
    totalItems: number;
    totalSkus: number;
  };
  stores: Store[];
  orderSearchTerm: string;
  setOrderSearchTerm: (term: string) => void;
  statusFilter: 'ALL' | OrderStatus;
  onStatusFilterChange: (status: 'ALL' | OrderStatus) => void;
  sortBy: SellerOrderSortBy;
  onSortByChange: (sortBy: SellerOrderSortBy) => void;
  statusCounts: {
    ALL: number;
    PENDING: number;
    READY: number;
    COMPLETED: number;
    CANCELLED: number;
  };
  onUpdateOrderStatus: (order: Order) => void;
  onUpdateOrderStatusBatch: (
    ids: string[],
    status: OrderStatus,
    modifierRole?: string,
    partialUpdates?: { id: string; itemIds: string[] }[],
  ) => Promise<void>;
  orderSheets: OrderSheet[];
  selectedOrderSheetIds: string[];
  onOrderSheetChange: (ids: string[]) => void;
  products: Product[]; // Need products to register
  currentPage: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  selectedStoreId?: string;
  onFetchAllOrdersForSelection?: () => Promise<Order[]>;
  onRefresh?: () => void;
  onViewReport?: (sheetId: string) => void;
  onSettle?: (storeId?: string) => void;
  customerAliases?: Record<string, string>;
  onOpenAdminOrder?: (name?: string, phone?: string, order?: Order, storeId?: string, orderSheetId?: string, userId?: string) => void;
  onViewHistory?: (name: string, phone: string, userId?: string, orderSheetId?: string) => void;
}

export const OrdersTab: React.FC<OrdersTabProps> = ({
  orders,
  ordersSummary,
  stores,
  orderSearchTerm,
  setOrderSearchTerm,
  statusFilter,
  onStatusFilterChange,
  sortBy,
  onSortByChange,
  statusCounts,
  onUpdateOrderStatus,
  onUpdateOrderStatusBatch,
  orderSheets,
  selectedOrderSheetIds,
  onOrderSheetChange,
  products,
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
  selectedStoreId,
  onFetchAllOrdersForSelection,
  onRefresh,
  onSettle,
  customerAliases,
  onOpenAdminOrder,
  onViewHistory,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [newSheet, setNewSheet] = useState<Partial<OrderSheet>>({
    label: '',
    deliveryDate: '',
    orderingStartDate: new Date().toISOString().split('T')[0],
    orderingEndDate: '',
    storeId: '',
  });

  const [activeReport, setActiveReport] = useState<ReportType>('ORDER_LIST');
  const [reportData, setReportData] = useState<any>(null);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editSheetData, setEditSheetData] = useState<Partial<OrderSheet>>({});
  const [isShowingHistory, setIsShowingHistory] = useState(false);
  const [packingWaitingOrders, setPackingWaitingOrders] = useState<Order[]>([]);
  const [isPackingWaitingLoading, setIsPackingWaitingLoading] = useState(false);
  const [packingWaitingError, setPackingWaitingError] = useState<string | null>(null);
  const [batchState, setBatchState] = useState<{
    isExporting: boolean;
    currentIndex: number;
    total: number;
    size: number;
  }>({ isExporting: false, currentIndex: 0, total: 0, size: 20 });

  const scopedStoreOrders = useMemo(
    () =>
      orders.filter(
        (order) => stores.find((store) => store.id === order.storeId)?.orderType === 'STORE',
      ),
    [orders, stores],
  );

  const hasStoreOrdersInScope = scopedStoreOrders.length > 0;

  const filteredReportData = useMemo(() => {
    if (!reportData) return null;

    let result = reportData;
    if (orderSearchTerm) {
      const term = orderSearchTerm.toLowerCase();
      if (activeReport === 'CUSTOMER_RECEIPTS' || activeReport === 'PACKING_LABELS') {
        result = result.filter((o: any) =>
          (o.customerName + (o.customerPhone || '')).toLowerCase().includes(term)
        );
      } else if (activeReport === 'FIELD_CHECKLIST') {
        result = (reportData || []).map((store: any) => ({
          ...store,
          orders: (store.orders || []).filter((o: any) =>
            (o.customerName + (o.customerPhone || '')).toLowerCase().includes(term)
          )
        })).filter((store: any) => (store.orders || []).length > 0);
      }
    }

    if (sortBy === 'name') {
      if (activeReport === 'CUSTOMER_RECEIPTS' || activeReport === 'PACKING_LABELS') {
        return [...result].sort((a, b) => (a.customerName || '').localeCompare(b.customerName || '', 'ko-KR'));
      } else if (activeReport === 'FIELD_CHECKLIST') {
        return result.map((store: any) => ({
          ...store,
          orders: [...(store.orders || [])].sort((a, b) => (a.customerName || '').localeCompare(b.customerName || '', 'ko-KR'))
        }));
      }
    }

    return result;
  }, [reportData, orderSearchTerm, activeReport, sortBy]);

  const sortedOrderSheets = useMemo(() => {
    if (!orderSheets) return [];
    return [...orderSheets].sort((a, b) => {
      const storeA = stores.find(s => s.id === a.storeId);
      const storeB = stores.find(s => s.id === b.storeId);

      const posA = storeA?.position ?? 999;
      const posB = storeB?.position ?? 999;

      if (posA !== posB) return posA - posB;

      return new Date(b.deliveryDate).getTime() - new Date(a.deliveryDate).getTime();
    });
  }, [orderSheets, stores]);

  const latestOrderSheetsPerStore = useMemo(() => {
    const map = new Map<string, OrderSheet>();
    sortedOrderSheets.forEach(sheet => {
      if (!map.has(sheet.storeId)) {
        map.set(sheet.storeId, sheet);
      }
    });
    return Array.from(map.values());
  }, [sortedOrderSheets]);

  useEffect(() => {
    if (activeReport === 'COMPLETED_PICKUP_LIST' && !hasStoreOrdersInScope) {
      setActiveReport('ORDER_LIST');
    }
  }, [activeReport, hasStoreOrdersInScope]);

  useEffect(() => {
    setReportData(null); // Clear data when switching reports or selections
    if (
      activeReport !== 'ORDER_LIST' &&
      activeReport !== 'PACKING_WAITING_LIST' &&
      activeReport !== 'COMPLETED_PICKUP_LIST' &&
      selectedOrderSheetIds &&
      selectedOrderSheetIds.length > 0 &&
      !selectedOrderSheetIds.includes('all')
    ) {
      fetchReportData();
    }
  }, [activeReport, selectedOrderSheetIds]);

  useEffect(() => {
    if (activeReport !== 'PACKING_WAITING_LIST') {
      return;
    }

    if (!selectedOrderSheetIds || selectedOrderSheetIds.length === 0) {
      setPackingWaitingOrders([]);
      return;
    }

    let isCancelled = false;

    const fetchPackingWaitingOrders = async () => {
      setIsPackingWaitingLoading(true);
      setPackingWaitingError(null);
      setPackingWaitingOrders([]);

      const orderSheetIds = selectedOrderSheetIds.includes('all')
        ? undefined
        : selectedOrderSheetIds;
      const allPendingOrders: Order[] = [];
      const pageSize = 100;
      let page = 1;

      try {
        while (true) {
          const result = await getSellerOrdersPage({
            orderSheetIds,
            search: orderSearchTerm,
            status: 'PENDING',
            page,
            take: pageSize,
            storeId: selectedStoreId,
          });

          if (isCancelled) {
            return;
          }

          allPendingOrders.push(...result.orders);

          if (!result.hasMore || page >= result.totalPages || result.orders.length === 0) {
            break;
          }

          page += 1;
        }

        setPackingWaitingOrders(allPendingOrders);
      } catch (error) {
        console.error('Failed to fetch packing waiting orders', error);
        if (!isCancelled) {
          setPackingWaitingOrders([]);
          setPackingWaitingError('포장 대기 목록을 불러오지 못했습니다.');
        }
      } finally {
        if (!isCancelled) {
          setIsPackingWaitingLoading(false);
        }
      }
    };

    void fetchPackingWaitingOrders();

    return () => {
      isCancelled = true;
    };
  }, [activeReport, orderSearchTerm, selectedOrderSheetIds, selectedStoreId]);

  const fetchReportData = async () => {
    if (
      activeReport === 'ORDER_LIST' ||
      activeReport === 'PACKING_WAITING_LIST' ||
      activeReport === 'COMPLETED_PICKUP_LIST'
    ) {
      return;
    }
    setIsReportLoading(true);
    const idsToFetch = selectedOrderSheetIds.includes('all')
      ? orderSheets.map(s => s.id)
      : selectedOrderSheetIds;

    try {
      const allResults = await Promise.all(
        idsToFetch.map(async (id) => {
          return await getCustomerReceipts(id, undefined);
        })
      );

      let combinedResult: any[] = [];
      allResults.forEach(res => {
        if (Array.isArray(res)) {
          combinedResult = [...combinedResult, ...res];
        }
      });

      if (activeReport === 'CUSTOMER_RECEIPTS' || activeReport === 'PACKING_LABELS') {
        setReportData(combinedResult);
      } else {
        const mergedStores: Record<string, any> = {};
        combinedResult.forEach(order => {
          if (!mergedStores[order.storeId]) {
            mergedStores[order.storeId] = {
              id: order.storeId,
              name: order.storeName || order.store?.name || '정보 없음',
              orders: [order]
            };
          } else {
            mergedStores[order.storeId].orders.push(order);
          }
        });
        setReportData(Object.values(mergedStores));
      }
    } catch (error) {
      console.error('Failed to fetch report data', error);
      setReportData(null);
    } finally {
      setIsReportLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    const element = document.getElementById('report-to-pdf');
    if (!element) return;

    const isFieldChecklist = activeReport === 'FIELD_CHECKLIST';
    const isPackingWaitingList = activeReport === 'PACKING_WAITING_LIST';
    const isCompletedPickupList = activeReport === 'COMPLETED_PICKUP_LIST';
    const isPackingLabelList = activeReport === 'PACKING_LABELS';
    if (isPackingLabelList) {
      element.classList.add('a4-label-pdf-export');
    } else if (isFieldChecklist || isPackingWaitingList || isCompletedPickupList) {
      element.classList.add('a4-pdf-export');
    } else {
      element.classList.add('bixolon-pdf-export');
    }

    // Let DOM update styling cleanly before rendering PDF
    await new Promise(r => setTimeout(r, 100));

    const isSingleSelection = selectedOrderSheetIds.length === 1 && !selectedOrderSheetIds.includes('all');
    const firstSelectedSheet = isSingleSelection ? orderSheets.find(s => s.id === selectedOrderSheetIds[0]) : undefined;
    const store = stores.find(st => st.id === firstSelectedSheet?.storeId);
    const storeName = isSingleSelection ? (store?.name || '전체 매장') : '다중매장';
    const label = isSingleSelection ? (firstSelectedSheet?.label || '전체 주문서') : '다중선택_주문서';

    const heightInPx = element.scrollHeight || element.offsetHeight || 1000;
    const heightInMm = Math.max((heightInPx * 25.4) / 96 + 30, 200);

    let filename = '';
    if (isFieldChecklist) filename = `${label}_현장배부체크리스트.pdf`;
    else if (isPackingWaitingList) filename = `${label}_포장대기목록.pdf`;
    else if (isPackingLabelList) filename = `${label}_포장라벨.pdf`;
    else if (isCompletedPickupList) filename = `${label}_수령완료목록.pdf`;
    else filename = `${label}_${storeName}_영수증.pdf`;

    const useA4Format = isFieldChecklist || isPackingWaitingList || isCompletedPickupList || isPackingLabelList;
    const format = useA4Format ? 'a4' : [72, heightInMm];

    const html2canvasOptions: any = {
      scale: 2,
      useCORS: true,
      logging: false,
      windowWidth: isPackingLabelList ? 794 : useA4Format ? 800 : 300,
      letterRendering: !isPackingLabelList,
      backgroundColor: '#ffffff',
    };

    html2canvasOptions.onclone = (clonedDocument: Document) => {
      const style = clonedDocument.createElement('style');
      style.textContent = `
        html,
        body {
          color: rgb(17, 24, 39) !important;
          background: rgb(255, 255, 255) !important;
        }
        *,
        *::before,
        *::after {
          color: rgb(17, 24, 39) !important;
          background-color: transparent !important;
          background-image: none !important;
          border-color: rgb(209, 213, 219) !important;
          outline-color: rgb(17, 24, 39) !important;
          text-decoration-color: rgb(17, 24, 39) !important;
          caret-color: rgb(17, 24, 39) !important;
          box-shadow: none !important;
          text-shadow: none !important;
        }
        #report-to-pdf,
        #report-to-pdf .bixolon-print-area,
        #report-to-pdf .field-checklist-area {
          background: rgb(255, 255, 255) !important;
        }
        ${isPackingLabelList ? PACKING_LABEL_STYLES : ''}
      `;
      clonedDocument.head.appendChild(style);
    };

    const opt = {
      margin: isPackingLabelList ? 0 : useA4Format ? [15, 2, 10, 2] : 1,
      filename: filename,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: html2canvasOptions,
      jsPDF: { unit: 'mm', format: format, orientation: 'portrait' },
      pagebreak: isPackingLabelList
        ? { mode: ['css', 'legacy'], after: '.packing-label-page' }
        : { mode: ['avoid-all', 'css', 'legacy'] }
    };

    try {
      await html2pdf().set(opt).from(element).save();
    } finally {
      element.classList.remove('bixolon-pdf-export');
      element.classList.remove('a4-pdf-export');
      element.classList.remove('a4-label-pdf-export');
    }
  };

  const handleDownloadBatchReceiptsPDF = async () => {
    if (!reportData || activeReport !== 'CUSTOMER_RECEIPTS') return;

    // We need to aggregate first to know the true count of receipts
    const aggregate = (list: any[], keyFn: (o: any) => string) => {
      const map: Record<string, any> = {};
      list.forEach(order => {
        const key = keyFn(order);
        if (!map[key]) {
          map[key] = { ...order };
        }
      });
      return Object.values(map);
    };

    const aggregated = aggregate(reportData, (o) => {
      const phoneVal = (o.customerPhone || '').replace(/\D/g, '');
      const sheetId = o.orderSheetId || o.storeId || '';
      const name = (o.customerName || '').trim();
      return `${sheetId}-${name}-${phoneVal}`;
    });
    const totalReceipts = aggregated.length;
    const batchSize = 20;
    const totalBatches = Math.ceil(totalReceipts / batchSize);

    if (totalBatches <= 1) {
      await handleDownloadPDF();
      return;
    }

    if (!confirm(`${totalReceipts}명의 영수증을 ${totalBatches}개의 PDF 파일로 나누어 내려받으시겠습니까?\n(브라우저 팝업 차단을 해제해야 여러 파일이 저장됩니다)`)) {
      return;
    }

    setBatchState({
      isExporting: true,
      currentIndex: 0,
      total: totalBatches,
      size: batchSize
    });

    try {
      for (let i = 0; i < totalBatches; i++) {
        setBatchState(prev => ({ ...prev, currentIndex: i + 1 }));

        // Let React re-render the view with the slice
        await new Promise(r => setTimeout(r, 500));

        const element = document.getElementById('report-to-pdf');
        if (!element) continue;

        element.classList.add('bixolon-pdf-export');
        await new Promise(r => setTimeout(r, 100));

        const isSingleSelection = selectedOrderSheetIds.length === 1 && !selectedOrderSheetIds.includes('all');
        const firstSelectedSheet = isSingleSelection ? orderSheets.find(s => s.id === selectedOrderSheetIds[0]) : undefined;
        const store = stores.find(st => st.id === firstSelectedSheet?.storeId);
        const storeName = isSingleSelection ? (store?.name || '전체 매장') : '다중매장';
        const label = isSingleSelection ? (firstSelectedSheet?.label || '전체 주문서') : '다중선택_주문서';

        const heightInPx = element.scrollHeight || element.offsetHeight || 1000;
        const heightInMm = Math.max((heightInPx * 25.4) / 96 + 30, 200);

        const filename = `${label}_${storeName}_영수증_Part${i + 1}.pdf`;

        const opt = {
          margin: 1,
          filename: filename,
          image: { type: 'jpeg', quality: 1 },
          html2canvas: { scale: 2, useCORS: true, logging: false, windowWidth: 300, letterRendering: true },
          jsPDF: { unit: 'mm', format: [72, heightInMm], orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        };

        await html2pdf().set(opt).from(element).save();
        element.classList.remove('bixolon-pdf-export');

        // Small delay between files to avoid browser congestion
        await new Promise(r => setTimeout(r, 1000));
      }
    } catch (error) {
      console.error('Batch export failed', error);
      alert('배치 다운로드 중 오류가 발생했습니다.');
    } finally {
      setBatchState(prev => ({ ...prev, isExporting: false }));
    }
  };

  const handleStoreChange = (storeId: string) => {
    if (!storeId) {
      setNewSheet(prev => ({
        ...prev,
        storeId: '',
        label: '',
        deliveryDate: '',
        orderingEndDate: '',
      }));
      return;
    }

    const store = stores.find(s => s.id === storeId);
    if (!store) return;

    // getDay() returns 0 for Sunday, 1 for Monday, ..., 6 for Saturday
    // Field priority: defaultDeliveryDay (user set) > deliveryDayOfWeek (schema default) > 5 (Friday)
    const deliveryDay = store.defaultDeliveryDay ?? store.deliveryDayOfWeek ?? 5;
    const orderCutoffDays = store.orderCutoffDays ?? 1;

    // Get current local date at midnight
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // 1. Calculate the upcoming delivery day (the closest deliveryDay >= today)
    let nextDelivery = new Date(today);
    let daysUntilNext = (deliveryDay - today.getDay() + 7) % 7;
    nextDelivery.setDate(today.getDate() + daysUntilNext);

    // 2. Check if the cutoff has passed for this delivery date
    const cutoffDate = new Date(nextDelivery);
    cutoffDate.setDate(nextDelivery.getDate() - orderCutoffDays);
    cutoffDate.setHours(0, 0, 0, 0);

    // If today is past or equal to the cutoff date, move to next week
    if (today.getTime() >= cutoffDate.getTime()) {
      nextDelivery.setDate(nextDelivery.getDate() + 7);
    }

    const storeSheets = [...(orderSheets || [])]
      .filter(s => s.storeId === storeId)
      .sort((a, b) => new Date(b.deliveryDate).getTime() - new Date(a.deliveryDate).getTime());

    if (storeSheets.length > 0) {
      const latestSheetDate = new Date(storeSheets[0].deliveryDate);
      latestSheetDate.setHours(0, 0, 0, 0);
      if (latestSheetDate.getTime() >= nextDelivery.getTime()) {
        nextDelivery.setDate(nextDelivery.getDate() + 7);
      }
    }

    const formatDate = (date: Date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    const dateStr = formatDate(nextDelivery);
    const todayStr = formatDate(now);

    const days = ['일', '월', '화', '수', '목', '금', '토'];
    const dayName = days[nextDelivery.getDay()];
    const label = `${nextDelivery.getMonth() + 1}/${nextDelivery.getDate()}(${dayName}) 주문지`;

    setNewSheet(prev => ({
      ...prev,
      storeId,
      label,
      deliveryDate: dateStr,
      orderingEndDate: dateStr + 'T23:59:59',
      orderingStartDate: todayStr,
    }));
  };

  const [isManagingProducts, setIsManagingProducts] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  const isSingleSelection = selectedOrderSheetIds.length === 1 && !selectedOrderSheetIds.includes('all');
  const selectedSheet = useMemo(() =>
    isSingleSelection ? orderSheets.find(s => s.id === selectedOrderSheetIds[0]) : undefined,
    [orderSheets, selectedOrderSheetIds, isSingleSelection]
  );

  const isLatestSheet = useMemo(() => {
    if (!selectedSheet) return false;
    const sameStoreSheets = orderSheets.filter(s => s.storeId === selectedSheet.storeId);
    if (sameStoreSheets.length === 0) return true;
    const maxDate = Math.max(...sameStoreSheets.map(s => new Date(s.deliveryDate).getTime()));
    return new Date(selectedSheet.deliveryDate).getTime() >= maxDate;
  }, [selectedSheet, orderSheets]);

  const filteredProductsForSheet = useMemo(
    () =>
      products.filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(productSearchTerm.toLowerCase());
        const matchesTag = !selectedTag || (p.tags && p.tags.includes(selectedTag));
        return matchesSearch && matchesTag;
      }),
    [products, productSearchTerm, selectedTag]
  );

  const selectedProductCount = selectedProductIds.length;

  useEffect(() => {
    if (selectedSheet?.products) {
      setSelectedProductIds(selectedSheet.products.map(p => p.id));
    } else {
      setSelectedProductIds([]);
    }
  }, [selectedSheet, isManagingProducts]);

  const summary = useMemo(() => {
    if (ordersSummary) {
      return ordersSummary;
    }

    const totalOrders = orders.length;
    const totalAmount = orders.reduce((sum, o) => sum + o.totalPrice, 0);
    const totalItems = orders.reduce(
      (sum, o) =>
        sum + (o.items || []).reduce((iSum, item) => iSum + item.quantity, 0),
      0,
    );
    const totalSkus = new Set(
      orders.flatMap((order) => (order.items || []).map((item) => item.name)),
    ).size;

    return { totalOrders, totalAmount, totalItems, totalSkus };
  }, [orders, ordersSummary]);

  const selectionScopeKey = useMemo(() => {
    const normalizedSheetKey = selectedOrderSheetIds.includes('all')
      ? 'all'
      : [...selectedOrderSheetIds].sort().join(',');

    return [
      normalizedSheetKey,
      orderSearchTerm.trim(),
      statusFilter,
      selectedStoreId || 'ALL',
      sortBy,
    ].join('::');
  }, [
    orderSearchTerm,
    selectedOrderSheetIds,
    selectedStoreId,
    sortBy,
    statusFilter,
  ]);

  const resetForm = () => {
    const now = new Date();
    const todayStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
    setNewSheet({
      label: '',
      deliveryDate: '',
      orderingStartDate: todayStr,
      orderingEndDate: '',
      storeId: '',
    });
    setIsCreating(false);
  };

  return (
    <div className="space-y-4 pb-20 print:p-0 print:space-y-0">
      {/* Order Cycle Selector */}
      <div className="bg-white p-4 rounded-3xl border shadow-sm space-y-3 print:hidden">
        <div className="flex justify-between items-center px-1">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-gray-400" />
            <h3 className="text-sm font-black text-gray-900">주문서 선택</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-xl text-xs font-black transition-colors hover:bg-emerald-100"
            >
              <Plus size={14} /> 새 주문서
            </button>
            <button
              onClick={() => {
                let storeId = '';
                let orderSheetId = '';
                if (selectedOrderSheetIds.length === 1 && selectedOrderSheetIds[0] !== 'all') {
                  const sheet = orderSheets.find(s => s.id === selectedOrderSheetIds[0]);
                  if (sheet) {
                    storeId = sheet.storeId;
                    orderSheetId = sheet.id;
                  }
                }
                onOpenAdminOrder?.('', '', undefined, storeId, orderSheetId);
              }}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-900 text-white rounded-xl text-xs font-black transition-colors hover:bg-gray-800 shadow-sm"
            >
              <Plus size={14} /> 대신 주문하기
            </button>
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 px-1">
          <button
            onClick={() => onOrderSheetChange(['all'])}
            className={`flex-shrink-0 px-4 py-2.5 rounded-2xl text-xs font-black transition-all ${selectedOrderSheetIds.includes('all')
              ? 'bg-gray-900 text-white shadow-md shadow-gray-200'
              : 'bg-white text-gray-400 border border-gray-100 hover:border-gray-200'
              }`}
          >
            전체 내역
          </button>
          {latestOrderSheetsPerStore.map(s => {
            const store = stores.find(st => st.id === s.storeId);
            const isSelected = selectedOrderSheetIds.length === 1 && selectedOrderSheetIds[0] === s.id;
            return (
              <button
                key={s.id}
                onClick={() => {
                  onOrderSheetChange([s.id]);
                }}
                className={`flex-shrink-0 px-4 py-2.5 rounded-2xl transition-all border text-left flex flex-col justify-center ${isSelected
                  ? 'bg-emerald-50 border-emerald-200 ring-1 ring-emerald-500/20 shadow-sm'
                  : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
              >
                <div className={`text-[10px] font-bold mb-0.5 ${isSelected ? 'text-emerald-600' : 'text-gray-400'}`}>
                  [{store?.name || '미지정'}] {s.label || '주문지'}
                </div>
                <div className={`text-xs font-black ${isSelected ? 'text-emerald-900' : 'text-gray-700'}`}>
                  {new Date(s.deliveryDate).toLocaleDateString('ko-KR')} 배송
                </div>
              </button>
            );
          })}
          <button
            onClick={() => setIsShowingHistory(true)}
            className="flex-shrink-0 px-4 py-2.5 rounded-2xl text-xs font-black transition-all bg-white text-gray-400 border border-gray-100 hover:border-gray-200 flex items-center gap-1.5"
          >
            <History size={14} /> 과거 이력
          </button>
        </div>
      </div>

      {isShowingHistory && (
        <div className="fixed inset-0 z-[6000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setIsShowingHistory(false)} />
          <div className="relative w-full max-w-sm bg-white rounded-[32px] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in duration-200 max-h-[80vh]">
            <div className="p-6 border-b flex justify-between items-center bg-gray-50/50">
              <h4 className="text-lg font-black">과거 주문서 이력</h4>
              <button onClick={() => setIsShowingHistory(false)} className="p-2 bg-white rounded-full text-gray-400 border"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto no-scrollbar p-2">
              <div className="space-y-1">
                {sortedOrderSheets.map(s => {
                  const store = stores.find(st => st.id === s.storeId);
                  const isSelected = selectedOrderSheetIds.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      onClick={() => {
                        onOrderSheetChange([s.id]);
                        setIsShowingHistory(false);
                      }}
                      className={`w-full p-4 rounded-2xl transition-all text-left flex justify-between items-center ${isSelected
                        ? 'bg-emerald-50 ring-1 ring-emerald-500/20'
                        : 'hover:bg-gray-50'
                        }`}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-black px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-md">
                            {store?.name || '미지정'}
                          </span>
                          <span className={`text-[10px] font-bold ${s.isClosed ? 'text-red-500' : 'text-emerald-500'}`}>
                            {s.isClosed ? '마감됨' : '진행중'}
                          </span>
                        </div>
                        <div className="text-sm font-black text-gray-900">
                          {s.label || '주문서'}
                        </div>
                        <div className="text-[11px] font-bold text-gray-400">
                          배송일: {new Date(s.deliveryDate).toLocaleDateString('ko-KR')}
                        </div>
                      </div>
                      <div className={`p-2 rounded-xl border ${isSelected ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-white text-gray-300'}`}>
                        <Check size={16} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t">
              <button
                onClick={() => {
                  onOrderSheetChange(['all']);
                  setIsShowingHistory(false);
                }}
                className="w-full h-12 bg-gray-900 text-white rounded-2xl font-black text-xs shadow-lg"
              >
                전체 이력 보기
              </button>
            </div>
          </div>
        </div>
      )}

      {isCreating && (
        <div className="bg-white p-5 rounded-3xl border shadow-lg space-y-4 animate-in fade-in slide-in-from-top-2">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-black">새 주문지 생성</h3>
            <button onClick={resetForm} className="text-gray-400"><X size={18} /></button>
          </div>
          <div className="space-y-3">
            <div className="space-y-3">
              {/* Row 1: Store and Label */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">배송지</label>
                  <select
                    className="w-full h-10 bg-gray-50 border rounded-xl px-2 text-[11px] font-bold outline-none"
                    value={newSheet.storeId}
                    onChange={e => handleStoreChange(e.target.value)}
                  >
                    <option value="">선택</option>
                    {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">레이블 (옵션)</label>
                  <input
                    className="w-full h-10 bg-gray-50 border rounded-xl px-2 text-[11px] font-bold outline-none"
                    placeholder="2월 3주차"
                    value={newSheet.label}
                    onChange={e => setNewSheet(prev => ({ ...prev, label: e.target.value }))}
                  />
                </div>
              </div>

              {/* Row 2: Delivery Date and Cutoff Time */}
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">배송 예정일</label>
                <input
                  type="date"
                  className="w-full h-10 bg-gray-50 border rounded-xl px-2 text-[11px] font-bold outline-none"
                  min={new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  value={newSheet.deliveryDate}
                  onChange={e => {
                    const selectedDate = new Date(e.target.value);
                    const store = stores.find(s => s.id === newSheet.storeId);
                    if (store) {
                      const targetDay = store.defaultDeliveryDay ?? store.deliveryDayOfWeek ?? 5;
                      const currentDay = selectedDate.getDay();

                      if (currentDay !== targetDay) {
                        const days = ['일', '월', '화', '수', '목', '금', '토'];
                        alert(`해당 배송지는 ${days[targetDay]}요일 배송입니다. 선택하신 날짜가 ${days[targetDay]}요일로 자동 조정됩니다.`);

                        // Snap to the nearest valid delivery day
                        const diff = (targetDay - currentDay + 7) % 7;
                        selectedDate.setDate(selectedDate.getDate() + (diff > 3 ? diff - 7 : diff));
                      }
                    }

                    const y = selectedDate.getFullYear();
                    const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
                    const d = String(selectedDate.getDate()).padStart(2, '0');
                    const dateStr = `${y}-${m}-${d}`;

                    // Update label accordingly
                    const days = ['일', '월', '화', '수', '목', '금', '토'];
                    const dayName = days[selectedDate.getDay()];
                    const label = `${selectedDate.getMonth() + 1}/${selectedDate.getDate()}(${dayName}) 주문지`;

                    setNewSheet(prev => ({
                      ...prev,
                      deliveryDate: dateStr,
                      label: label,
                      orderingEndDate: `${dateStr}T23:59:59`
                    }));
                  }}
                />
              </div>
            </div>

            {newSheet.storeId && (
              <div className="bg-emerald-50/50 rounded-2xl p-4 space-y-2 border border-emerald-100">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-emerald-600 uppercase">예약된 일정 확인</span>
                  <Info size={12} className="text-emerald-400" />
                </div>
                <div className="space-y-1">
                  <p className="text-[9px] font-bold text-gray-400 mb-0.5">배송 예정 일정</p>
                  <p className="text-xs font-black text-gray-900">
                    {newSheet.deliveryDate} <span className="text-emerald-600 ml-1">배송 예정</span>
                  </p>
                </div>
              </div>
            )}

            <button
              onClick={async () => {
                if (!newSheet.storeId || !newSheet.deliveryDate || !newSheet.orderingEndDate) {
                  alert('필수 정보를 모두 입력해주세요.');
                  return;
                }
                const { createOrderSheet } = await import('../../../api/order-sheets');
                try {
                  await createOrderSheet(newSheet);
                  alert('주문지가 생성되었습니다.');
                  resetForm();
                  if (onRefresh) onRefresh();
                } catch (e) {
                  console.error(e);
                  alert('생성 실패');
                }
              }}
              className="w-full h-11 bg-emerald-500 text-white rounded-xl font-black text-xs shadow-lg shadow-emerald-100"
            >
              주문지 시작하기
            </button>
          </div>
        </div>
      )}

      <OrderSummaryCard
        summary={summary}
        selectedOrderSheetIds={selectedOrderSheetIds}
        selectedSheet={selectedSheet}
        stores={stores}
        isLatestSheet={isLatestSheet}
        onRefresh={onRefresh}
        onSettle={onSettle}
        onOrderSheetChange={onOrderSheetChange}
        setIsManagingProducts={setIsManagingProducts}
        setIsEditing={setIsEditing}
        setEditSheetData={setEditSheetData}
      />

      {isEditing && (
        <div className="fixed inset-0 z-[6000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setIsEditing(false)} />
          <div className="relative w-full max-w-sm bg-white rounded-[32px] p-6 space-y-6 flex flex-col shadow-2xl animate-in zoom-in duration-200">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="text-lg font-black">주문서 수정</h4>
              </div>
              <button onClick={() => setIsEditing(false)} className="p-2 bg-gray-100 rounded-full text-gray-400"><X size={18} /></button>
            </div>

            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase">레이블</label>
                <input
                  className="w-full h-10 bg-gray-50 border rounded-xl px-2 text-[11px] font-bold outline-none"
                  value={editSheetData.label}
                  onChange={e => setEditSheetData(prev => ({ ...prev, label: e.target.value }))}
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase">배송 예정일</label>
                <input
                  type="date"
                  className="w-full h-10 bg-gray-50 border rounded-xl px-2 text-[11px] font-bold outline-none"
                  value={editSheetData.deliveryDate}
                  onChange={e => {
                    const newDate = e.target.value;
                    setEditSheetData(prev => ({
                      ...prev,
                      deliveryDate: newDate,
                      orderingEndDate: `${newDate}T23:59:59`
                    }));
                  }}
                />
              </div>

              <button
                onClick={async () => {
                  const { updateOrderSheet } = await import('../../../api/order-sheets');
                  try {
                    await updateOrderSheet(editSheetData.id!, {
                      label: editSheetData.label,
                      deliveryDate: editSheetData.deliveryDate,
                      orderingEndDate: editSheetData.orderingEndDate,
                    });
                    alert('수정되었습니다.');
                    setIsEditing(false);
                    if (onRefresh) onRefresh();
                  } catch (e) {
                    console.error(e);
                    alert('수정 실패');
                  }
                }}
                className="w-full h-12 bg-gray-900 text-white rounded-2xl font-black text-xs shadow-xl"
              >
                수정 완료
              </button>
            </div>
          </div>
        </div>
      )}

      {isManagingProducts && selectedSheet && (
        <div className="fixed inset-0 z-[6000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setIsManagingProducts(false)} />
          <div className="relative w-full max-w-sm bg-white rounded-[32px] p-6 space-y-6 flex flex-col max-h-[80vh] shadow-2xl animate-in zoom-in duration-200">
            <div className="flex justify-between items-center">
              <div className="space-y-2">
                <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-black">
                  선택 {selectedProductCount}개
                </div>
                <h4 className="text-lg font-black">{selectedSheet.label || '주문서'} 상품 설정</h4>
                <p className="text-[10px] font-bold text-gray-400 mt-0.5">이 주문서에서 판매할 상품을 선택하세요.</p>
              </div>
              <button onClick={() => setIsManagingProducts(false)} className="p-2 bg-gray-100 rounded-full text-gray-400"><X size={18} /></button>
            </div>

            <div className="space-y-3 shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input
                  type="text"
                  placeholder="상품명 검색..."
                  className="w-full h-9 bg-gray-50 border rounded-xl pl-9 pr-3 text-[11px] font-bold outline-none border-gray-100"
                  value={productSearchTerm}
                  onChange={e => setProductSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
                <button
                  onClick={() => setSelectedTag(null)}
                  className={`px-3 h-7 rounded-lg text-[10px] font-bold whitespace-nowrap border transition-all ${!selectedTag ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-400 border-gray-200'}`}
                >
                  전체
                </button>
                <div className="w-[1px] h-4 bg-gray-200 mx-1 shrink-0" />
                <button
                  onClick={() => {
                    const filteredProductIds = filteredProductsForSheet.map(p => p.id);
                    setSelectedProductIds(prev => Array.from(new Set([...prev, ...filteredProductIds])));
                  }}
                  className="px-3 h-7 rounded-lg text-[10px] font-bold whitespace-nowrap border bg-white text-emerald-600 border-emerald-100 hover:bg-emerald-50 transition-all"
                >
                  전체 선택
                </button>
                <button
                  onClick={() => {
                    const filteredProductIds = filteredProductsForSheet.map(p => p.id);
                    setSelectedProductIds(prev => prev.filter(id => !filteredProductIds.includes(id)));
                  }}
                  className="px-3 h-7 rounded-lg text-[10px] font-bold whitespace-nowrap border bg-white text-red-500 border-red-100 hover:bg-red-50 transition-all"
                >
                  전체 해제
                </button>
                <div className="w-[1px] h-4 bg-gray-200 mx-1 shrink-0" />
                {Array.from(new Set(products.flatMap(p => p.tags || []))).map(tag => (
                  <button
                    key={tag}
                    onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                    className={`px-3 h-7 rounded-lg text-[10px] font-bold whitespace-nowrap border transition-all ${selectedTag === tag ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-400 border-gray-200'}`}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar space-y-2 py-1">
              {filteredProductsForSheet
                .map(p => {
                  const isSelected = selectedProductIds.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      onClick={() => {
                        setSelectedProductIds(prev =>
                          prev.includes(p.id) ? prev.filter(id => id !== p.id) : [...prev, p.id]
                        );
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-2xl border transition-all ${isSelected ? 'bg-emerald-50 border-emerald-500 ring-1 ring-emerald-500/20' : 'bg-gray-50 border-transparent hover:bg-gray-100'}`}
                    >
                      <img src={p.image} className="w-10 h-10 rounded-lg object-cover border bg-white" alt="" />
                      <div className="flex-1 text-left">
                        <p className={`text-xs font-black ${isSelected ? 'text-emerald-900' : 'text-gray-900'}`}>{p.name}</p>
                        <p className="text-[10px] font-bold text-gray-400">{p.price.toLocaleString()}원</p>
                      </div>
                      {isSelected ? (
                        <div className="w-5 h-5 bg-emerald-500 text-white rounded-full flex items-center justify-center shadow-sm shadow-emerald-200">
                          <Check size={12} strokeWidth={3} />
                        </div>
                      ) : (
                        <div className="w-5 h-5 border-2 border-gray-200 rounded-full" />
                      )}
                    </button>
                  );
                })}
              {products.length > 0 && filteredProductsForSheet.length === 0 && (
                <div className="py-10 text-center space-y-2">
                  <p className="text-[11px] font-bold text-gray-400">검색 결과가 없습니다.</p>
                </div>
              )}
            </div>

            <button
              onClick={async () => {
                const { registerProductsToOrderSheet } = await import('../../../api/order-sheets');
                try {
                  await registerProductsToOrderSheet(selectedSheet.id, selectedProductIds);
                  alert('상품 구성이 저장되었습니다.');
                  setIsManagingProducts(false);
                  if (onRefresh) onRefresh();
                } catch (e) {
                  console.error(e);
                  alert('저장 실패');
                }
              }}
              className="w-full h-12 bg-gray-900 text-white rounded-2xl font-black text-xs shadow-xl"
            >
              선택한 {selectedProductCount}개 저장하기
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3 print:hidden">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              value={orderSearchTerm}
              onChange={e => setOrderSearchTerm(e.target.value)}
              placeholder="고객명 또는 연락처 검색"
              className="w-full h-12 bg-white border rounded-2xl pl-11 pr-4 text-sm font-bold shadow-sm outline-none"
            />
          </div>
          <button
            onClick={() => onSortByChange(sortBy === 'name' ? 'default' : 'name')}
            className={`flex items-center gap-2 px-4 h-12 rounded-2xl border font-black transition-all text-xs whitespace-nowrap shadow-sm ${sortBy === 'name'
              ? 'bg-gray-900 text-white border-gray-900'
              : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'}`}
            title="정렬 기준 전환"
          >
            <ListFilter size={16} />
            {sortBy === 'name' ? '이름순' : '최신순'}
          </button>
        </div>
      </div>


      <div className="w-full bg-white overflow-hidden flex flex-col rounded-[32px] border shadow-sm mt-6 print:shadow-none print:rounded-none print:p-0">
        {/* Header - Hidden in Print */}
        <div className="p-4 sm:p-6 border-b flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 bg-gray-50 print:hidden rounded-t-[32px]">
          <div className="flex-1 w-full">
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
              <button
                onClick={() => setActiveReport('ORDER_LIST')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black transition-all whitespace-nowrap ${activeReport === 'ORDER_LIST' ? 'bg-gray-900 text-white' : 'bg-white border text-gray-500 hover:bg-gray-50'}`}
              >
                <ClipboardList size={14} /> 주문 내역
              </button>
              {hasStoreOrdersInScope && (
                <button
                  onClick={() => setActiveReport('COMPLETED_PICKUP_LIST')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black transition-all whitespace-nowrap ${activeReport === 'COMPLETED_PICKUP_LIST' ? 'bg-gray-900 text-white' : 'bg-white border text-gray-500 hover:bg-gray-50'}`}
                >
                  <ShoppingBag size={14} /> 수령 완료 목록
                </button>
              )}
              <button
                onClick={() => setActiveReport('CUSTOMER_RECEIPTS')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black transition-all whitespace-nowrap ${activeReport === 'CUSTOMER_RECEIPTS' ? 'bg-gray-900 text-white' : 'bg-white border text-gray-500 hover:bg-gray-50'}`}
              >
                <User size={14} /> 고객 영수증
              </button>
              <button
                onClick={() => setActiveReport('PACKING_WAITING_LIST')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black transition-all whitespace-nowrap ${activeReport === 'PACKING_WAITING_LIST' ? 'bg-gray-900 text-white' : 'bg-white border text-gray-500 hover:bg-gray-50'}`}
              >
                <Package size={14} /> 포장 대기 목록
              </button>
              <button
                onClick={() => setActiveReport('PACKING_LABELS')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black transition-all whitespace-nowrap ${activeReport === 'PACKING_LABELS' ? 'bg-gray-900 text-white' : 'bg-white border text-gray-500 hover:bg-gray-50'}`}
              >
                <FileText size={14} /> 포장 라벨 목록
              </button>

              <button
                onClick={() => setActiveReport('FIELD_CHECKLIST')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black transition-all whitespace-nowrap ${activeReport === 'FIELD_CHECKLIST' ? 'bg-gray-900 text-white' : 'bg-white border text-gray-500 hover:bg-gray-50'}`}
              >
                <Check size={14} /> 현장 배부표
              </button>
            </div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto overflow-x-auto no-scrollbar pb-1">
            {activeReport !== 'ORDER_LIST' && (
              <div className="flex gap-2">
                {activeReport === 'CUSTOMER_RECEIPTS' && reportData && reportData.length > 20 && (
                  <button
                    onClick={handleDownloadBatchReceiptsPDF}
                    disabled={batchState.isExporting}
                    className="flex p-3 bg-emerald-50 border border-emerald-100 rounded-2xl hover:bg-emerald-100 transition-colors items-center gap-2 text-sm font-bold text-emerald-600 disabled:opacity-50"
                  >
                    <Download size={18} /> {batchState.isExporting ? `생성 중 (${batchState.currentIndex}/${batchState.total})` : '배치 내려받기(20명씩)'}
                  </button>
                )}
                {activeReport === 'FIELD_CHECKLIST' ? (
                  <button
                    onClick={handlePrint}
                    className="flex p-3 bg-gray-900 text-white rounded-2xl shadow-lg hover:bg-gray-800 transition-colors items-center gap-2 text-sm font-black whitespace-nowrap"
                  >
                    <Printer size={18} /> 체크리스트 인쇄
                  </button>
                ) : (
                  <button
                    onClick={handleDownloadPDF}
                    disabled={batchState.isExporting}
                    className="hidden sm:flex p-3 bg-white border rounded-2xl hover:bg-gray-50 transition-colors items-center gap-2 text-sm font-bold text-gray-500 disabled:opacity-50"
                    title="PDF 대신 직접 인쇄를 권장합니다."
                  >
                    <Download size={18} /> {activeReport === 'CUSTOMER_RECEIPTS' ? '한번에 내려받기' : activeReport === 'PACKING_LABELS' ? '라벨 내려받기' : activeReport === 'PACKING_WAITING_LIST' || activeReport === 'COMPLETED_PICKUP_LIST' ? '목록 내려받기' : '내려받기'}
                  </button>
                )}
              </div>
            )}
            {activeReport !== 'FIELD_CHECKLIST' && (
              <button onClick={handlePrint} className="flex-1 sm:flex-none p-3 bg-gray-900 text-white rounded-2xl shadow-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 text-xs sm:text-sm font-black whitespace-nowrap">
                <Printer size={18} /> {activeReport === 'PACKING_LABELS' ? '라벨 인쇄' : activeReport === 'PACKING_WAITING_LIST' || activeReport === 'COMPLETED_PICKUP_LIST' ? '목록 인쇄' : '영수증 인쇄'}
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-8 bg-white print:overflow-visible print:p-0">
          {activeReport === 'ORDER_LIST' ? (
            <div className="report-content" id="report-to-pdf">
              <OrderListView
                orders={orders || []}
                totalCount={totalCount}
                currentPage={currentPage}
                totalPages={totalPages}
                statusFilter={statusFilter}
                onStatusFilterChange={onStatusFilterChange}
                statusCounts={statusCounts}
                onUpdateOrderStatus={onUpdateOrderStatus}
                onUpdateOrderStatusBatch={onUpdateOrderStatusBatch}
                customerAliases={customerAliases}
                onOpenAdminOrder={onOpenAdminOrder}
                onViewHistory={onViewHistory}
                sortBy={sortBy}
                products={products}
                onPageChange={onPageChange}
                onFetchAllOrdersForSelection={onFetchAllOrdersForSelection}
                selectionScopeKey={selectionScopeKey}
              />
            </div>
          ) : activeReport === 'COMPLETED_PICKUP_LIST' ? (
            <div className="report-content a4-report" id="report-to-pdf">
              <CompletedPickupListView
                orders={scopedStoreOrders}
                customerAliases={customerAliases}
                onViewHistory={onViewHistory}
                sortBy={sortBy}
                searchTerm={orderSearchTerm}
              />
            </div>
          ) : activeReport === 'PACKING_WAITING_LIST' && isPackingWaitingLoading ? (
            <div className="flex items-center justify-center py-20 text-gray-400 font-bold">포장 대기 목록을 불러오는 중...</div>
          ) : activeReport === 'PACKING_WAITING_LIST' && packingWaitingError ? (
            <div className="flex items-center justify-center py-20 text-gray-400 font-bold">{packingWaitingError}</div>
          ) : isReportLoading ? (
            <div className="flex items-center justify-center py-20 text-gray-400 font-bold">데이터를 불러오는 중...</div>
          ) : selectedOrderSheetIds.length === 0 ? (
            <div className="flex items-center justify-center py-20 text-gray-400 font-bold">주문지를 선택해야 리포트를 조회할 수 있습니다.</div>
          ) : (activeReport !== 'PACKING_WAITING_LIST' && (!reportData || (Array.isArray(reportData) && reportData.length === 0))) ? (
            <div className="flex items-center justify-center py-20 text-gray-400 font-bold">데이터가 없습니다.</div>
          ) : (activeReport !== 'PACKING_WAITING_LIST' && (Array.isArray(filteredReportData) && filteredReportData.length === 0)) ? (
            <div className="flex items-center justify-center py-20 text-gray-400 font-bold">검색 결과가 없습니다.</div>
          ) : (
            <div className={`report-content ${activeReport === 'FIELD_CHECKLIST' || activeReport === 'PACKING_WAITING_LIST' ? 'a4-report' : ''} ${activeReport === 'PACKING_LABELS' ? 'packing-label-report' : ''}`} id="report-to-pdf">
              {activeReport === 'CUSTOMER_RECEIPTS' && (
                <CustomerReceiptsView
                  data={filteredReportData}
                  customerAliases={customerAliases}
                  orderSheets={orderSheets}
                  batchRange={batchState.isExporting ? { start: (batchState.currentIndex - 1) * batchState.size, end: batchState.currentIndex * batchState.size } : undefined}
                  sortBy={sortBy}
                />
              )}
              {activeReport === 'PACKING_WAITING_LIST' && <PackingWaitingListView orders={packingWaitingOrders} />}
              {activeReport === 'PACKING_LABELS' && <PackingLabelsView data={filteredReportData} customerAliases={customerAliases} orderSheets={orderSheets} sortBy={sortBy} />}
              {activeReport === 'FIELD_CHECKLIST' && <FieldChecklistView data={filteredReportData} customerAliases={customerAliases} orderSheets={orderSheets} sortBy={sortBy} />}
            </div>
          )}
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        @media print {
          @page { size: auto; margin: 0; }
          .a4-report {
            @page {
              size: A4;
              margin: 20mm 15mm;
            }
          }
          .checklist-row {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          /* Hide EVERYTHING by default in print */
          body * { visibility: hidden; }
          /* Only show the report content and its parents */
          .report-content, .report-content *, 
          .field-checklist-area, .field-checklist-area * { 
            visibility: visible !important; 
          }
          .report-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          
          body { visibility: visible !important; overflow: visible !important; background: white !important; }
          .print\\\\:hidden { display: none !important; }
          div[class*="fixed"], div[class*="absolute"], div[class*="relative"] {
            position: static !important; overflow: visible !important; width: auto !important; height: auto !important;
            max-height: none !important; box-shadow: none !important; background: transparent !important;
            padding: 0 !important; margin: 0 !important; transform: none !important;
          }
          .bixolon-print-area, .bixolon-print-area * { visibility: visible !important; color: black !important; }
          .bixolon-print-area { display: block !important; width: 72mm !important; margin: 0 auto !important; padding: 2mm !important; background: white !important; box-sizing: border-box; }
          .field-checklist-area { display: block !important; width: 100% !important; background: white !important; }
          .page-break { page-break-after: always; }
          .report-content { display: block !important; }
        }
        .bixolon-pdf-export {
            display: block !important;
            width: 72mm !important;
            background: white !important;
            margin: 0;
            padding: 0;
            overflow: hidden !important;
        }
        .a4-pdf-export {
            display: block !important;
            width: 210mm !important;
            background: white !important;
            margin: 0;
            padding: 10mm;
        }
        .bixolon-pdf-export .bixolon-print-area {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            color: black !important;
            width: 72mm !important;
            margin: 0 auto !important;
            padding: 2mm !important;
            line-height: 1.4 !important; /* Fix for html2canvas text clipping */
            box-sizing: border-box;
        }
        .bixolon-pdf-export .page-break { page-break-after: auto; border-bottom: 1px dashed #000; padding-bottom: 8mm; margin-bottom: 8mm; }
        .bixolon-pdf-export .break-inside-avoid { break-inside: avoid; }
        
        /* PDF specific optimizations */
        .field-checklist-area tr {
            page-break-inside: avoid;
            break-inside: avoid;
        }
        .field-checklist-area td {
            box-decoration-break: clone;
            -webkit-box-decoration-break: clone;
        }
      `}} />

    </div>
  );
};




// const ProductSummaryView = ({ data }: { data: any[] }) => {
//   // The 'data' for ProductSummaryView is expected to be an array of aggregated products.
//   // Each product item should have: id, name, shortName, quantity, tags (array of strings).
//   const products = data || [];

//   return (
//     <div className="space-y-6">
//       <div className="text-center space-y-2 mb-10">
//         <h2 className="text-2xl font-black">상품별 발주 집계표</h2>
//         <p className="text-sm font-bold text-gray-400">전체 배송 물량 확인 및 피킹용</p>
//       </div>
//       <div className="divide-y divide-gray-100 border-y border-gray-100">
//         {products.map((p, idx) => (
//           <div key={p.id || idx} className="p-4 border-b last:border-0 hover:bg-gray-50 transition-colors">
//             <div className="flex justify-between items-start">
//               <div>
//                 <div className="text-[8px] font-black text-emerald-600 mb-1">
//                   {p.tags?.map((t: string) => `#${t}`).join(' ') || '태그 없음'}
//                 </div>
//                 <h4 className="text-xs font-black text-gray-900">{p.name}</h4>
//                 <p className="text-[10px] font-bold text-gray-400 mt-0.5">{p.shortName}</p>
//               </div>
//               <div className="text-right">
//                 <p className="text-xs font-black text-gray-900">{(p.quantity || 0).toLocaleString()}개</p>
//               </div>
//             </div>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// };

// const aggregateOrders = (orders: any[]) => {
//   if (!orders) return [];

//   const aggregated: Record<string, any> = {};

//   orders.forEach(order => {
//     // Create a unique key for grouping (name + phone)
//     // You might want to include storeId if you want to separate by store, 
//     // but for "Customer Receipts" typically it's per customer regardless of store?
//     // Actually, the original code had `order.store?.name` so it seems per-order.
//     // If we want to aggregate "per customer" (e.g. one receipt for John Doe containing all his items from all orders),
//     // we should group by name + phone.
//     // However, if the requirement is "customer receipt" it often implies one shipment.
//     // If the user says "Separate by store", we should include storeId.
//     // The original `CustomerReceiptsView` iterated over `data` which `reports.service.ts` says returns `order[]`.
//     // The `DeliverySummaryView` iterates over stores, then `store.orders`.

//     // Let's assume aggregation is by (Customer Name + Customer Phone).
//     // If a customer ordered 2 times, we merge them.
//     // Note: The original `CustomerReceiptsView` showed `order.store?.name`. If we merge orders across stores, which store name do we show?
//     // The service returns orders sorted by `storeId` then `customerName`.
//     // If we merge across stores, we might lose store info or need to list all stores.
//     // Given the context "주문자별 영수증", it usually means "What this person ordered in total".
//     // Let's group by name+phone.

//     const key = `${order.customerName}-${order.customerPhone}`;

//     if (!aggregated[key]) {
//       aggregated[key] = {
//         ...order,
//         items: [...(order.items || [])],
//         // Keep the first ID as reference or join them
//         id: order.id,
//         // Determine how to handle multiple stores if present. 
//         // For now, we'll keep the first store info, or maybe comma separate?
//         // But usually aggregation is within the same context or we just list items.
//       };
//     } else {
//       // Merge items
//       aggregated[key].items = [...aggregated[key].items, ...(order.items || [])];
//       // Sum price
//       aggregated[key].totalPrice = (aggregated[key].totalPrice || 0) + (order.totalPrice || 0);
//       // Append ID for reference?
//       aggregated[key].id = `${aggregated[key].id}, ${order.id}`;
//     }
//   });

//   // We might want to consolidate items with same productId inside the aggregated order too?
//   // "주문자별로 상품을 묶어서" might imply this too.
//   // Let's also consolidate items within the aggregated order.

//   return Object.values(aggregated).map(order => {
//     const itemMap: Record<string, any> = {};
//     order.items.forEach((item: any) => {
//       if (!itemMap[item.productId]) {
//         itemMap[item.productId] = { ...item };
//       } else {
//         itemMap[item.productId].quantity += item.quantity;
//       }
//     });
//     return {
//       ...order,
//       items: Object.values(itemMap)
//     };
//   });
// };
